import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { companiesApi, getApiErrorMessage } from "../api";
import { Plus, X, FileCheck, Building2, MapPin, History as HistoryIcon, Briefcase, Users, Clock, CheckCircle2, Trash2, Info } from "lucide-react";
import Dialog from "../components/common/Dialog";
import FormField, { TextInput } from "../components/common/FormField";
import ProvisionedAccessCard from "../components/common/ProvisionedAccessCard";
import ActionMenu from "../components/common/ActionMenu";
import PageIntro from "../components/ui/PageIntro";
import CompanyHistoryDialog from '../components/companies/CompanyHistoryDialog';
import RepresentativeSignatures from '../components/companies/RepresentativeSignatures';
import { useConfirm } from "../store/ConfirmContext";
import { useAuth } from "../store/AuthContext";
import { canonicalRole, ROLES } from "../utils/permissions";
import InternationalPhoneInput from "../components/common/InternationalPhoneInput";
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import FormDraftNotice from '../components/common/FormDraftNotice';
import CountrySelect from '../components/common/CountrySelect';
import { COMPANY_CATEGORIES, companyCategories } from '../utils/companyCategories';

const emptyForm = { 
  name: "", 
  legal_name: "", 
  country_code: "RU",
  correspondence_language: 'ru',
  inn: "", 
  kpp: "", 
  legal_address: "", 
  production_sites: [], 
  site_address: "", 
  director_name: "", 
  category_types: [], 
  business_activity_type: "", 
  compliance_requirements: {
    legal_status: null,
    financial_stability: null,
    reputation: null
  },
  notes: "" 
};

const CATEGORY_OPTIONS = COMPANY_CATEGORIES;

const emptyMemberInvite = {
  email: "",
  full_name: "",
  phone: "",
  representative_title: "",
  note: "",
};

const emptyInitialRepresentative = {
  email: "",
  full_name: "",
  phone: "",
  representative_title: "",
};

const COMPLIANCE_CRITERIA = [
  { 
    key: 'legal_status', 
    label: uiText("Юридическая состоятельность"),
    items: [
      uiText("Юридическое лицо зарегистрировано"),
      uiText("Статус действующее"),
      uiText("Нет процедур ликвидации/банкротства"),
      uiText("ОКВЭД соответствует деятельности"),
      uiText("Есть необходимые лицензии/СРО"),
      uiText("Нет в реестре недобросовестных поставщиков")
    ]
  },
  { 
    key: 'financial_stability', 
    label: uiText("Финансовая устойчивость"),
    items: [
      uiText("Бухотчетность представлена"),
      uiText("Нет отрицательных чистых активов"),
      uiText("Нет просрочки по налогам"),
      uiText("Выручка сопоставима с объемом")
    ]
  },
  { 
    key: 'reputation', 
    label: uiText("Репутация и опыт"),
    items: [
      uiText("Референс-лист представлен"),
      uiText("Нет судебных актов о нарушениях")
    ]
  }
];

export default function CompaniesPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const canDeleteCompanies = canonicalRole(user) === ROLES.ADMIN;
  const [items, setItems] = useState([]);
  const emptyCompanyDraft = { company: emptyForm, representative: emptyInitialRepresentative, withoutRepresentative: false };
  const companyEditor = useRecordFormDraft(user?.id, 'registry', 'company', emptyCompanyDraft);
  const { buffer: companyBuffer, recordId: editingId } = companyEditor;
  const form = companyEditor.form.company;
  const setForm = update => companyEditor.setForm(current => ({ ...current, company: typeof update === 'function' ? update(current.company) : update }));
  const [savingCompany, setSavingCompany] = useState(false);
  const [error, setError] = useState("");
  const [showCompliance, setShowCompliance] = useState(false);
  const initialRepresentative = companyEditor.form.representative;
  const setInitialRepresentative = update => companyEditor.setForm(current => ({ ...current, representative: typeof update === 'function' ? update(current.representative) : update }));
  const withoutInitialRepresentative = companyEditor.form.withoutRepresentative;
  const setWithoutInitialRepresentative = value => companyEditor.setForm(current => ({ ...current, withoutRepresentative: value }));
  const [lastProvisionedAccess, setLastProvisionedAccess] = useState(null);

  // Фильтр по типу контрагента.
  // Historical category codes are resolved as display aliases.
  // Чип «all» — показать все компании.
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [historyCompany, setHistoryCompany] = useState(null);
  const [membersCompany, setMembersCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const memberEditor = useRecordFormDraft(user?.id, membersCompany?.id || 'closed', 'company-member', emptyMemberInvite);
  const { form: memberInviteForm, setForm: setMemberInviteForm, buffer: memberBuffer } = memberEditor;
  const [memberInviteSaving, setMemberInviteSaving] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [lastMemberInvite, setLastMemberInvite] = useState(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [notice, setNotice] = useState('');
  const [activeCompanyStep, setActiveCompanyStep] = useState('basic');
  const [deletingCompanyId, setDeletingCompanyId] = useState(null);

  const companyFormSteps = [
    { key: 'basic', label: uiText("Данные компании"), complete: Boolean(form.name && form.inn) },
    ...(!editingId ? [{
      key: 'representative',
      label: uiText("Первый представитель"),
      complete: withoutInitialRepresentative || Boolean(initialRepresentative.full_name && initialRepresentative.email && initialRepresentative.representative_title),
    }] : []),
    { key: 'profile', label: uiText("Профиль"), complete: Boolean((form.category_types || []).length) },
    { key: 'addresses', label: uiText("Площадки"), complete: Boolean(form.legal_address || form.site_address) },
    { key: 'review', label: uiText("Проверка"), complete: Boolean(form.name && form.inn && (editingId || withoutInitialRepresentative || initialRepresentative.email)) },
  ];

  const goToCompanyStep = (key) => {
    setActiveCompanyStep(key);
    document.getElementById(`company-form-step-${key}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const load = async () => {
    try {
      const res = await companiesApi.list();
      setItems(res.data || []);
    }
    catch (e) { setError(getApiErrorMessage(e)); }
  };

  // Отфильтрованный список с учётом чипа типа и поиска
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (partnerFilter !== 'all') {
        const cats = companyCategories(it.category_types);
        if (!cats.includes(partnerFilter)) return false;
      }
      if (term) {
        const haystack = [it.name, it.legal_name, it.inn, it.business_activity_type,
          ...(it.representative_contacts || []).flatMap(contact => [contact.full_name, contact.email, contact.phone])]
          .filter(Boolean).map((s) => String(s).toLowerCase()).join(' ');
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [items, partnerFilter, search]);

  // Сводка по типам — для бейджей-счётчиков сверху над таблицей
  const partnerCounts = useMemo(() => {
    const counts = { all: items.length, supplier: 0, partner: 0, untyped: 0 };
    for (const it of items) {
      const cats = companyCategories(it.category_types);
      if (cats.length === 0) counts.untyped += 1;
      for (const c of cats) {
        if (counts[c] !== undefined) counts[c] += 1;
      }
    }
    return counts;
  }, [items]);

  const openHistory = company => setHistoryCompany(company);
  const closeHistory = () => setHistoryCompany(null);

  const loadMembers = async (company) => {
    if (!company?.id) return;
    setMembersLoading(true);
    setMembersError('');
    try {
      const res = await companiesApi.members(company.id);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMembersError(getApiErrorMessage(e) || uiText("Не удалось загрузить сотрудников"));
    } finally {
      setMembersLoading(false);
    }
  };

  const openMembers = async (company) => {
    setMembersCompany(company);
    setMembers([]);
    setLastMemberInvite(null);
    setLastProvisionedAccess(null);
    await loadMembers(company);
  };

  const closeMembers = () => {
    if (memberInviteSaving || signatureSaving) return;
    setMembersCompany(null);
    setMembers([]);
    setMembersError('');
    setLastMemberInvite(null);
    setLastProvisionedAccess(null);
  };

  const submitMemberInvite = async (e) => {
    e.preventDefault();
    if (!membersCompany?.id || memberInviteSaving || memberBuffer.recovery) return;
    const snapshot = memberBuffer.snapshot();
    setMemberInviteSaving(true);
    setMembersError('');
    try {
      const res = await companiesApi.createMember(membersCompany.id, {
        ...memberInviteForm,
      });
      setLastMemberInvite(res.data?.member || null);
      setLastProvisionedAccess({
        ...res.data?.credentials,
        ...res.data?.delivery,
      });
      memberEditor.saved(snapshot);
      await loadMembers(membersCompany);
      await load();
    } catch (e2) {
      setMembersError(getApiErrorMessage(e2) || uiText("Не удалось добавить сотрудника"));
    } finally {
      setMemberInviteSaving(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (savingCompany || companyBuffer.recovery) return;
    const snapshot = companyBuffer.snapshot();
    setSavingCompany(true);
    setError("");
    try {
      if (editingId) {
        await companiesApi.update(editingId, form);
        setNotice(uiText("Изменения в карточке компании сохранены."));
      } else {
        const response = await companiesApi.create({
          ...form,
          initial_representative: withoutInitialRepresentative ? null : initialRepresentative,
        });
        const access = response.data?.initial_representative;
        setLastProvisionedAccess({
          ...access?.credentials,
          ...access?.delivery,
          full_name: access?.member?.full_name,
        });
        setNotice(
          !access
            ? uiText("Компания создана без представителя. Аудит будет работать во внутреннем режиме; представителя можно добавить позже из карточки компании.")
            : access?.delivery?.status === 'smtp_not_configured'
            ? uiText("Компания и первый представитель созданы. Отправка почты пока не настроена — сохраните показанные данные доступа.")
            : uiText("Компания и первый представитель созданы. Письмо с доступом отправляется автоматически — дополнительных действий не требуется.")
        );
      }
      if (companyEditor.saved(snapshot)) {
        setShowCompliance(false);
        setShowCompanyForm(false);
      }
      await load();
    } catch (e2) { setError(getApiErrorMessage(e2)); }
    finally { setSavingCompany(false); }
  };
  
  const edit = async (item) => {
    if (savingCompany || !await companyEditor.begin(item.id, { ...emptyCompanyDraft, company: {
      ...Object.fromEntries(Object.keys(emptyForm).map(key => [key, item[key] ?? emptyForm[key]])),
      category_types: companyCategories(item.category_types),
      country_code: item.country_code || '',
      production_sites: item.production_sites || [],
      compliance_requirements: item.compliance_requirements || emptyForm.compliance_requirements
    } })) return;
    setShowCompanyForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  const startNewCompany = async () => {
    if (savingCompany) return;
    if (showCompanyForm && !editingId) { setShowCompanyForm(false); return; }
    if (!await companyEditor.begin(null, emptyCompanyDraft)) return;
    setLastProvisionedAccess(null); setShowCompliance(false); setShowCompanyForm(true);
  };
  const startForeignCompany = async () => {
    if (savingCompany || !await companyEditor.begin(null, { ...emptyCompanyDraft, company: { ...emptyForm, country_code: '' } })) return;
    setLastProvisionedAccess(null); setShowCompliance(false); setShowCompanyForm(true);
  };

  const removeCompany = async (item) => {
    if (!canDeleteCompanies || !item?.id || deletingCompanyId) return;
    const approved = await confirm(
      uiText("Удалить компанию «{v0}»? Вместе с ней будут удалены только неиспользованные тестовые учётные записи представителей и неотправленные данные доступа. Если компания уже участвует в рабочих процессах, система остановит удаление.", {v0: (item.name)}),
      {
        title: uiText("Удаление компании"),
        confirmLabel: uiText("Удалить компанию"),
        cancelLabel: uiText("Отмена"),
        danger: true,
      },
    );
    if (!approved) return;

    setDeletingCompanyId(item.id);
    setError('');
    setNotice('');
    try {
      const response = await companiesApi.delete(item.id);
      setNotice(response.data?.message || uiText("Компания «{v0}» удалена.", {v0: (item.name)}));
      if (editingId === item.id) {
        companyBuffer.discard();
        companyBuffer.hydrate({});
        setShowCompanyForm(false);
      }
      await load();
    } catch (deleteError) {
      setError(getApiErrorMessage(deleteError) || uiText("Не удалось удалить компанию"));
    } finally {
      setDeletingCompanyId(null);
    }
  };
  
  const toggleCategory = (value) => {
    const current = form.category_types || [];
    if (current.includes(value)) {
      setForm({ ...form, category_types: current.filter(c => c !== value) });
    } else {
      setForm({ ...form, category_types: [...current, value] });
    }
  };
  
  const addProductionSite = () => {
    setForm({ 
      ...form, 
      production_sites: [...(form.production_sites || []), { address: '', type: '', notes: '' }] 
    });
  };
  
  const removeProductionSite = (index) => {
    const sites = [...(form.production_sites || [])];
    sites.splice(index, 1);
    setForm({ ...form, production_sites: sites });
  };
  
  const updateProductionSite = (index, field, value) => {
    const sites = [...(form.production_sites || [])];
    sites[index] = { ...sites[index], [field]: value };
    setForm({ ...form, production_sites: sites });
  };
  
  const updateCompliance = (key, value) => {
    setForm({
      ...form,
      compliance_requirements: {
        ...(form.compliance_requirements || {}),
        [key]: value
      }
    });
  };
  
  return <div className="workspace-page">
    <PageIntro
      icon={Building2}
      title={uiText("Компании и контрагенты")}
      description={uiText("Организации, их представители и данные для назначения аудитов.")}
      aside={<div className="qualification-actions"><button type="button" className="btn btn-secondary" disabled={savingCompany} onClick={startForeignCompany}>{uiText('Иностранный контрагент')}</button><button type="button" className="btn btn-primary" disabled={savingCompany} onClick={startNewCompany}><Plus size={15} /> {showCompanyForm && !editingId ? uiText("Скрыть форму") : uiText("Новая компания")}</button></div>}
    />
    
    {error && <div className="alert alert-error">{error}</div>}
    <FormDraftNotice buffer={companyBuffer} label={uiText("Компания")} hidden={!showCompanyForm} onResume={() => setShowCompanyForm(true)} disabled={savingCompany} />
    {notice && <div className="alert alert-success companies-success-notice"><CheckCircle2 size={16} /><span>{notice}</span><button type="button" className="btn btn-ghost btn-sm" onClick={() => setNotice('')} aria-label={uiText("Закрыть уведомление")}>✕</button></div>}
    {!membersCompany && <ProvisionedAccessCard access={lastProvisionedAccess} onClose={() => setLastProvisionedAccess(null)} />}
    
    <div className={showCompanyForm ? 'workspace-grid-two companies-workspace-grid is-editing' : 'companies-workspace-grid'}>
      {showCompanyForm && (
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            {editingId ? uiText("Редактирование контрагента") : uiText("Новый контрагент")}
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
            <fieldset className="ap-form-fields" disabled={savingCompany || !!companyBuffer.recovery} style={{ display: 'grid', gap: 20 }}>
            <div className="form-stepper" aria-label={uiText("Этапы формы компании")}>
              {companyFormSteps.map((step, index) => (
                <button
                  key={step.key}
                  type="button"
                  className={`form-stepper-button ${activeCompanyStep === step.key ? 'is-active' : ''} ${step.complete ? 'is-complete' : ''}`}
                  onClick={() => goToCompanyStep(step.key)}
                  aria-current={activeCompanyStep === step.key ? 'step' : undefined}
                >
                  <span className="form-stepper-number">
                    {step.complete ? <CheckCircle2 size={13} /> : index + 1}
                  </span>
                  {step.label}
                </button>
              ))}
            </div>

            {/* Основная информация */}
            <div id="company-form-step-basic" className="form-step-anchor-block" style={{ display: 'grid', gap: 12 }}>
              <div className="workspace-section-label">{uiText("Основная информация")}</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required" htmlFor="company-name">{uiText("Название")}</label>
                  <input 
                    id="company-name"
                    className="form-control" 
                    value={form.name} 
                    onChange={e=>setForm({ ...form, name: e.target.value })} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="company-legal-name">{uiText("Юр. наименование")}</label>
                  <input 
                    id="company-legal-name"
                    className="form-control" 
                    value={form.legal_name} 
                    onChange={e=>setForm({ ...form, legal_name: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="company-country">{uiText("Страна регистрации организации")}</label>
                  <CountrySelect id="company-country" className="form-control" value={form.country_code || ''} required onChange={e => setForm({ ...form, country_code: e.target.value })} />
                  {form.country_code && form.country_code !== 'RU' && <p className="form-hint"><strong>{uiText("Иностранный контрагент.")}</strong>{uiText(" Укажите налоговый номер своей страны. ФНС России не используется.")}</p>}
                  <div className="form-hint">{uiText("Для иностранного контрагента выберите его страну. Источники проверки зависят от страны: ФНС РФ не проверяет иностранные организации.")}</div>
                </div>
                <div className="form-group"><label className="form-label" htmlFor="company-language">{uiText('Язык регистрационных писем')}</label><select id="company-language" className="form-control" value={form.correspondence_language || 'ru'} onChange={e => setForm({ ...form, correspondence_language: e.target.value })}><option value="ru">Русский</option><option value="az">Azərbaycan dili</option><option value="en">English</option></select><p className="form-hint">{uiText('Письма новым представителям будут на этом языке. По умолчанию — русский; страна регистрации не меняет язык автоматически.')}</p></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="company-tax-id">{uiText("ИНН / налоговый номер")}</label>
                  <input 
                    id="company-tax-id"
                    className="form-control" 
                    value={form.inn || ''} 
                    onChange={e=>setForm({ ...form, inn: e.target.value })} 
                    maxLength={12}
                    placeholder={uiText("До 12 символов")}
                    title={uiText("До 12 букв, цифр и других символов")}
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  <div className="form-hint">{uiText("Необязательно для внутреннего офиса или при отсутствии номера. Для иностранных компаний допустимы буквы и разделители.")}</div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="company-kpp">{uiText("КПП")}</label>
                  <input 
                    id="company-kpp"
                    className="form-control" 
                    value={form.kpp} 
                    onChange={e=>setForm({ ...form, kpp: e.target.value })} 
                    placeholder={uiText("9 цифр")}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="company-director">{uiText("Руководитель")}</label>
                  <input 
                    id="company-director"
                    className="form-control" 
                    value={form.director_name} 
                    onChange={e=>setForm({ ...form, director_name: e.target.value })} 
                  />
                </div>
              </div>
            </div>

            {!editingId && (
              <div id="company-form-step-representative" className="company-representative-section form-step-anchor-block">
                <div className="workspace-section-label"><Users size={14} />{uiText(" Первый представитель компании")}</div>
                <label className="company-no-representative-toggle">
                  <input
                    type="checkbox"
                    checked={withoutInitialRepresentative}
                    onChange={(event) => setWithoutInitialRepresentative(event.target.checked)}
                  />
                  <span>
                    <strong>{uiText("У компании пока нет представителя")}</strong>
                    <small>{uiText("Создать только карточку организации без пользователя и отправки письма.")}</small>
                  </span>
                </label>
                {withoutInitialRepresentative ? (
                  <div className="company-internal-workflow-note">
                    <Info size={17} />
                    <span><strong>{uiText("Без контактных данных.")}</strong>{uiText(" Карточку можно использовать для назначения аудита без пользователя и почты. Контакты появятся после добавления представителя. Процесс уже созданных аудитов не изменится.")}</span>
                  </div>
                ) : <p className="workspace-muted">{uiText("Контакты представителя используются как контакты компании. Система создаст логин и временный пароль и отправит их представителю.")}</p>}
                {!withoutInitialRepresentative && <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required" htmlFor="representative-name">{uiText("ФИО представителя")}</label>
                    <input id="representative-name" className="form-control" required value={initialRepresentative.full_name} onChange={(e) => setInitialRepresentative({ ...initialRepresentative, full_name: e.target.value })} placeholder={uiText("Иванов Иван Иванович")} />
                  </div>
                  <div className="form-group">
                    <label className="form-label required" htmlFor="representative-title">{uiText("Должность")}</label>
                    <input id="representative-title" className="form-control" required value={initialRepresentative.representative_title} onChange={(e) => setInitialRepresentative({ ...initialRepresentative, representative_title: e.target.value })} placeholder={uiText("Ответственный за аудит")} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required" htmlFor="representative-email">{uiText("Email представителя")}</label>
                    <input id="representative-email" className="form-control" type="email" required value={initialRepresentative.email} onChange={(e) => setInitialRepresentative({ ...initialRepresentative, email: e.target.value })} placeholder="employee@example.ru" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{uiText("Телефон")}</label>
                    <InternationalPhoneInput value={initialRepresentative.phone} onValueChange={(phone) => setInitialRepresentative({ ...initialRepresentative, phone })} />
                  </div>
                </div>
                </>}
              </div>
            )}
            {editingId && (
              <p className="workspace-muted">{uiText("Контакты компании берутся из учётных записей представителей. Просмотреть их и добавить представителя можно через меню «Сотрудники» в реестре компаний.")}</p>
            )}
            
            {/* Категории контрагента */}
            <div id="company-form-step-profile" className="form-step-anchor-block" style={{ display: 'grid', gap: 12 }}>
              <div className="workspace-section-label">
                <Building2 size={14} style={{ marginRight: 6 }} />{uiText("Категории контрагента")}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CATEGORY_OPTIONS.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`btn ${(form.category_types || []).includes(cat.value) ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => toggleCategory(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Вид деятельности */}
            <div className="form-group">
              <label className="form-label">{uiText("Вид деятельности")}</label>
              <input 
                className="form-control" 
                value={form.business_activity_type} 
                onChange={e=>setForm({ ...form, business_activity_type: e.target.value })}
                placeholder={uiText("Например: производитель запорной арматуры, дистрибьютер насосов...")}
              />
            </div>
            
            {/* Адреса */}
            <div id="company-form-step-addresses" className="form-step-anchor-block" style={{ display: 'grid', gap: 12 }}>
              <div className="workspace-section-label">
                <MapPin size={14} style={{ marginRight: 6 }} />{uiText("Адреса")}</div>
              <div className="form-group">
                <label className="form-label">{uiText("Юридический/фактический адрес")}</label>
                <input 
                  className="form-control" 
                  value={form.legal_address} 
                  onChange={e=>setForm({ ...form, legal_address: e.target.value })} 
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">{uiText("Адрес проверяемой площадки")}</label>
                <input 
                  className="form-control" 
                  value={form.site_address} 
                  onChange={e=>setForm({ ...form, site_address: e.target.value })} 
                />
              </div>
              
              {/* Производственные площадки */}
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label className="form-label" style={{ margin: 0 }}>{uiText("Производственные площадки / склады")}</label>
                  <button 
                    type="button" 
                    className="btn btn-outline btn-sm"
                    onClick={addProductionSite}
                  >
                    <Plus size={14} />{uiText(" Добавить")}</button>
                </div>
                
                {(form.production_sites || []).map((site, index) => (
                  <div key={index} style={{ 
                    display: 'grid', 
                    gap: 8, 
                    padding: 12, 
                    border: '1px solid var(--border)', 
                    borderRadius: 8,
                    background: 'var(--bg-secondary)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: 12 }}>{uiText("Площадка #")}{index + 1}</strong>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeProductionSite(index)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <input
                      className="form-control"
                      placeholder={uiText("Адрес")}
                      value={site.address || ''}
                      onChange={e => updateProductionSite(index, 'address', e.target.value)}
                    />
                    <div className="form-row">
                      <input
                        className="form-control"
                        placeholder={uiText("Тип (цех, склад, офис...)")}
                        value={site.type || ''}
                        onChange={e => updateProductionSite(index, 'type', e.target.value)}
                      />
                      <input
                        className="form-control"
                        placeholder={uiText("Примечание")}
                        value={site.notes || ''}
                        onChange={e => updateProductionSite(index, 'notes', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Базовые требования к благонадежности */}
            <div id="company-form-step-review" className="form-step-anchor-block" style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="workspace-section-label">
                  <FileCheck size={14} style={{ marginRight: 6 }} />{uiText("Базовые требования к благонадежности")}</div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowCompliance(!showCompliance)}
                >
                  {showCompliance ? uiText("Скрыть") : uiText("Показать")}
                </button>
              </div>
              
              {showCompliance && (
                <div style={{ display: 'grid', gap: 16, padding: 16, background: 'var(--bg-secondary)', borderRadius: 12 }}>
                  {COMPLIANCE_CRITERIA.map(section => (
                    <div key={section.key} style={{ display: 'grid', gap: 8 }}>
                      <strong style={{ fontSize: 13 }}>{section.label}</strong>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {section.items.map((item, idx) => (
                          <label key={idx} style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: 8, 
                            fontSize: 13,
                            cursor: 'pointer'
                          }}>
                            <input 
                              type="checkbox"
                              style={{ marginTop: 2 }}
                              checked={form.compliance_requirements?.[section.key]?.[idx] || false}
                              onChange={e => {
                                const current = form.compliance_requirements?.[section.key] || {};
                                updateCompliance(section.key, { ...current, [idx]: e.target.checked });
                              }}
                            />
                            <span>{item}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Примечания */}
            <div className="form-group">
              <label className="form-label">{uiText("Дополнительные примечания")}</label>
              <textarea 
                className="form-control" 
                rows={3} 
                value={form.notes} 
                onChange={e=>setForm({ ...form, notes: e.target.value })} 
              />
            </div>
            
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {editingId && (
                <button 
                  type="button" 
                  className="btn btn-outline" 
                  onClick={() => { 
                    setShowCompliance(false);
                    setShowCompanyForm(false);
                  }}
                >{uiText("Отменить")}</button>
              )}
              <button className="btn btn-primary" type="submit">
                {editingId ? uiText("Сохранить изменения") : uiText("Создать контрагента")}
              </button>
            </div>
            </fieldset>
          </form>
        </div>
      </div>
      )}
      
      <div className="card">
        <div className="card-header">
          <div className="card-title">{uiText("Реестр контрагентов (")}{filteredItems.length}{uiText(" из ")}{items.length})</div>
        </div>
        <div className="card-body">
          {/* Фильтры по типу партнёра + поиск.
              Партнёры сети теперь ведутся как обычные компании с category_types=partner. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <input
              className="form-control"
              style={{ maxWidth: 300 }}
              placeholder={uiText("Поиск по названию, ИНН, email…")}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="ap-control-options">
              <button type="button"
                className="ap-button ap-button--secondary ap-control-action" aria-pressed={partnerFilter === 'all'}
                onClick={() => setPartnerFilter('all')}
              >{uiText("Все · ")}{partnerCounts.all}</button>
              {CATEGORY_OPTIONS.map(opt => (
                <button type="button"
                  key={opt.value}
                  className="ap-button ap-button--secondary ap-control-action" aria-pressed={partnerFilter === opt.value}
                  onClick={() => setPartnerFilter(opt.value)}
                >
                  {opt.label} · {partnerCounts[opt.value] || 0}
                </button>
              ))}
            </div>
          </div>

          <div className="table-wrap company-registry-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{uiText("Компания")}</th>
                  <th>{uiText("ИНН")}</th>
                  <th>{uiText("Категории")}</th>
                  <th>{uiText("Контакты представителей")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr
                    key={item.id}
                    data-clickable="true"
                    tabIndex={0}
                    onClick={() => openMembers(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openMembers(item);
                      }
                    }}
                    aria-label={uiText("Открыть сотрудников организации {v0}", {v0: (item.name)})}
                  >
                    <td>
                      <strong>{item.name}</strong>
                      {item.legal_name && (
                        <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 2 }}>
                          {item.legal_name}
                        </div>
                      )}
                      {item.business_activity_type && (
                        <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 2 }}>
                          {item.business_activity_type}
                        </div>
                      )}
                      <div className="company-member-count" style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Users size={12} />{uiText(" Сотрудников: ")}{item.member_count || 0}
                      </div>
                    </td>
                    <td>
                      <code>{item.inn || '—'}</code>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {companyCategories(item.category_types).map(cat => {
                          const catOption = CATEGORY_OPTIONS.find(o => o.value === cat);
                          return catOption ? (
                            <span key={cat} className="ui-badge ui-badge-neutral" style={{ fontSize: 10 }}>
                              {catOption.label}
                            </span>
                          ) : null;
                        })}
                        {(!item.category_types || item.category_types.length === 0) && '—'}
                      </div>
                    </td>
                    <td>
                      {(item.representative_contacts || []).length ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          {item.representative_contacts.map(contact => (
                            <div key={contact.id}>
                              <div>{contact.full_name}</div>
                              <div className="workspace-muted">{contact.email}</div>
                              {contact.phone && <div className="workspace-muted">{contact.phone}</div>}
                            </div>
                          ))}
                        </div>
                      ) : <span className="workspace-muted">{uiText("Без представителя")}</span>}
                    </td>
                    <td className="company-registry-actions" onClick={(event) => event.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/suppliers/${item.id}`)}>{uiText("Профиль")}</button>}
                      <button type="button"
                        className="btn btn-primary btn-sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          const params = new URLSearchParams();
                          params.set('audited_company_id', String(item.id));
                          if (item.name) params.set('title', `Аудит «${item.name}»`);
                          navigate(`${process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? '/audit-preparation' : '/audits/new'}?${params.toString()}`);
                        }}
                        aria-label={uiText("Назначить аудит компании {v0}", {v0: (item.name)})}
                      >
                        <Briefcase size={14}/>{uiText(" Назначить")}</button>
                      <ActionMenu
                        label={uiText("Другие действия с компанией {v0}", {v0: (item.name)})}
                        actions={[
                          ...(process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' ? [{ key: 'profile', label: uiText("Работы, требования и квалификация"), icon: Building2, onClick: () => navigate(`/suppliers/${item.id}`) }] : []),
                          { key: 'members', label: uiText("Сотрудники"), icon: Users, onClick: () => openMembers(item) },
                          { key: 'history', label: uiText("История организации"), icon: HistoryIcon, onClick: () => openHistory(item) },
                          { key: 'edit', label: uiText("Изменить компанию"), onClick: () => edit(item) },
                          canDeleteCompanies ? {
                            key: 'delete',
                            label: deletingCompanyId === item.id ? uiText("Удаляем…") : uiText("Удалить компанию"),
                            icon: Trash2,
                            danger: true,
                            disabled: deletingCompanyId === item.id,
                            onClick: () => removeCompany(item),
                          } : null,
                        ]}
                      />
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan="5">
                      <div className="workspace-empty-state">
                        <div className="workspace-empty-state-title">{uiText("Компании по выбранным условиям не найдены")}</div>
                        <div className="workspace-empty-state-text">{uiText("Измените строку поиска или сбросьте категорию. Если организации ещё нет, зарегистрируйте её и первого представителя.")}</div>
                        <div className="workspace-side-actions" style={{ marginTop: 12 }}>
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSearch(''); setPartnerFilter('all'); }}>{uiText("Сбросить фильтры")}</button>
                          <button type="button" className="btn btn-primary btn-sm" disabled={savingCompany} onClick={startNewCompany}>{uiText("Новая компания")}</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    {/* Модалка сотрудников организации */}
    {membersCompany && (
      <Dialog size="lg" label={uiText("Сотрудники организации")} onClose={closeMembers} closeDisabled={memberInviteSaving || signatureSaving}>
          <div className="ap-dialog__header">
            <div>
              <h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Сотрудники организации")}</h2>
              <div className="ap-dialog__description">{membersCompany.name}</div>
            </div>
            <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} onClick={closeMembers} disabled={memberInviteSaving || signatureSaving}><X size={16} /></button>
          </div>
          <div className="ap-dialog__body">
            {membersError && <div className="alert alert-error">{membersError}</div>}
            {lastMemberInvite?.username && (
              <div className="alert" style={{ background: '#ecfdf3', border: '1px solid #bbf7d0', color: '#166534' }}>{uiText("Сотрудник добавлен в организацию и должен сменить пароль после первого входа.")}</div>
            )}
            <ProvisionedAccessCard access={lastProvisionedAccess} />

            <form className="ap-invite-form" onSubmit={submitMemberInvite}>
              <FormDraftNotice buffer={memberBuffer} label={uiText("Приглашение сотрудника")} disabled={memberInviteSaving} />
              <fieldset className="ap-form-fields" disabled={memberInviteSaving || !!memberBuffer.recovery}>
              <div className="ap-dialog__section-title"><Users size={14} aria-hidden="true" />{uiText("Пригласить представителя компании")}</div>
              <div className="workspace-muted">{uiText("Введите данные сотрудника — логин и временный пароль система создаст автоматически.")}</div>
              <div className="ap-form-grid">
                <FormField label="Email">
                  <TextInput
                    type="email"
                    required
                    autoComplete="off"
                    name="company_member_email"
                    value={memberInviteForm.email}
                    onChange={e => setMemberInviteForm({ ...memberInviteForm, email: e.target.value })}
                    placeholder="employee@example.ru"
                  />
                </FormField>
                <FormField label={uiText("ФИО")}>
                  <TextInput
                    required
                    autoComplete="off"
                    name="company_member_full_name"
                    value={memberInviteForm.full_name}
                    onChange={e => setMemberInviteForm({ ...memberInviteForm, full_name: e.target.value })}
                    placeholder={uiText("Иванов Иван")}
                  />
                </FormField>
              </div>
              <div className="ap-form-grid ap-form-grid--three">
                <FormField label={uiText("Должность")}>
                  <TextInput
                    required
                    autoComplete="off"
                    name="company_member_title"
                    value={memberInviteForm.representative_title}
                    onChange={e => setMemberInviteForm({ ...memberInviteForm, representative_title: e.target.value })}
                    placeholder={uiText("Ответственный за ПКПД")}
                  />
                </FormField>
                <FormField label={uiText("Телефон")}>
                  <InternationalPhoneInput
                    className="ap-control"
                    autoComplete="off"
                    name="company_member_phone"
                    value={memberInviteForm.phone}
                    onValueChange={(phone) => setMemberInviteForm({ ...memberInviteForm, phone })}
                  />
                </FormField>
                <FormField label={uiText("Заметка")}>
                  <TextInput
                    autoComplete="off"
                    name="company_member_note"
                    value={memberInviteForm.note}
                    onChange={e => setMemberInviteForm({ ...memberInviteForm, note: e.target.value })}
                    placeholder={uiText("Необязательно")}
                  />
                </FormField>
              </div>
              <div className="company-member-form-actions">
                <button className="ap-button ap-button--primary" type="submit" disabled={memberInviteSaving}>
                  {memberInviteSaving ? uiText("Создаём доступ…") : uiText("Создать и отправить доступ")}
                </button>
              </div>
              </fieldset>
            </form>

            {membersLoading ? (
              <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>
            ) : members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 28, color: 'var(--premium-text-secondary)', border: '1px dashed var(--border)', borderRadius: 8 }}>{uiText("В организации пока нет сотрудников")}</div>
            ) : (
              <div className="ap-table-wrap">
                <table className="ap-table responsive-table">
                  <thead>
                    <tr>
                      <th>{uiText("Сотрудник")}</th>
                      <th>{uiText("Контакты")}</th>
                      <th>{uiText("Роль")}</th>
                      <th>{uiText("Статус")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const ready = member.status === 'registered' && !member.must_change_password;
                      return (
                        <tr key={member.id || member.email}>
                          <td data-label={uiText("Сотрудник")}>
                            <div style={{ fontWeight: 700 }}>{member.full_name || member.email}</div>
                            {member.username && (
                              <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', marginTop: 2 }}>{uiText("Логин: ")}{member.username}</div>
                            )}
                            {member.representative_title && (
                              <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', marginTop: 2 }}>{member.representative_title}</div>
                            )}
                          </td>
                          <td data-label={uiText("Контакты")}>
                            <div>{member.email}</div>
                            {member.phone && <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', marginTop: 2 }}>{member.phone}</div>}
                          </td>
                          <td data-label={uiText("Роль")}><span className="ap-badge ap-badge--neutral">{member.role === 'auditee' ? uiText("Проверяемый") : member.role}</span></td>
                          <td data-label={uiText("Статус")}>
                            <span className={`ap-badge ${ready ? 'ap-badge--success' : 'ap-badge--warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              {ready ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                              {member.must_change_password ? uiText("Ожидает смену пароля") : ready ? uiText("Активен") : uiText("Заблокирован")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <RepresentativeSignatures company={membersCompany} members={members} onBusyChange={setSignatureSaving} />
          </div>
          <div className="ap-dialog__footer">
            <button type="button" className="ap-button ap-button--secondary" onClick={closeMembers} disabled={memberInviteSaving || signatureSaving}>{uiText("Закрыть")}</button>
          </div>
      </Dialog>
    )}

    {historyCompany && <CompanyHistoryDialog key={historyCompany.id} company={historyCompany} onClose={closeHistory} onAssign={() => {
      const params = new URLSearchParams({audited_company_id:String(historyCompany.id),title:`Аудит «${historyCompany.name}»`});
      closeHistory(); navigate(`${process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? '/audit-preparation' : '/audits/new'}?${params.toString()}`);
    }} />}
  </div>;
}
